import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AnalyticsService } from './analytics.service';
import {
  buildRouteName,
  currentLocationPath,
  sanitizePath,
} from './rum-route.util';

/** Name of the custom event carrying the full page-load timing breakdown. */
export const NAVIGATION_TIMING_EVENT_NAME = 'NavigationTiming';

/** Name of the metric tracking full page-load time (ms). */
export const NAVIGATION_TIMING_LOAD_METRIC = 'NavigationTiming/Load';

/**
 * Reports the initial **page load** timing breakdown from the
 * `PerformanceNavigationTiming` entry — full load, DOM interactive, DOM content
 * loaded, response start/end (TTFB), and transfer sizes.
 *
 * One event is emitted per page load (not one per field) to control telemetry
 * volume, plus a `NavigationTiming/Load` metric for percentile aggregation of
 * full load time. This complements SPA route tracking (which covers in-app
 * navigations) — it describes the cold document load. No-ops when the
 * Navigation Timing API is unavailable.
 */
@Injectable()
export class NavigationTimingService {
  private analytics = inject(AnalyticsService);

  private router = inject(Router, { optional: true });

  private reported = false;

  /**
   * Reports navigation timing once the page has finished loading. Safe to call
   * multiple times; only the first complete load reports.
   */
  public init(): void {
    if (
      this.reported ||
      typeof performance === 'undefined' ||
      typeof performance.getEntriesByType !== 'function'
    ) {
      return;
    }

    if (document.readyState === 'complete') {
      this.report();
    } else {
      window.addEventListener('load', () => this.report(), { once: true });
    }
  }

  /**
   * Reads the navigation timing entry and emits the metric + event.
   */
  private report(): void {
    if (this.reported) {
      return;
    }
    const [nav] = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    if (!nav) {
      return;
    }
    this.reported = true;

    const round = (value: number): number => Math.max(0, Math.round(value));
    const properties = {
      load: round(nav.loadEventEnd),
      domInteractive: round(nav.domInteractive),
      domContentLoaded: round(nav.domContentLoadedEventEnd),
      responseStart: round(nav.responseStart),
      responseEnd: round(nav.responseEnd),
      ttfb: round(nav.responseStart),
      transferSize: nav.transferSize,
      encodedBodySize: nav.encodedBodySize,
      navigationType: nav.type,
      routeName: buildRouteName(this.router),
      page: sanitizePath(currentLocationPath()),
    };

    this.analytics.trackMetric({
      name: NAVIGATION_TIMING_LOAD_METRIC,
      average: properties.load,
      properties,
    });

    this.analytics.trackEvent({
      name: NAVIGATION_TIMING_EVENT_NAME,
      properties,
    });
  }
}
